import * as THREE from 'three';
import { DRUID_PATH } from '@somnhold/content';
import { boughsLayout, gapOpenAt, hexToWorld, hollowLayout, sapFlow, sapLayout, type BattleState, type Bough, type BoughsLayout, type HollowLayout, type MapDef, type Pad, type SapLayout } from '@somnhold/sim';
import { Kit } from '../seasons/kit.ts';
import { lookPoint, Precip, type OmenCtx } from '../omens/common.ts';
import { WORLD_PER_HEX } from './coords.ts';
import { GlowLayer } from './glow.ts';
import { SkyDome } from './snow.ts';
import type { TerrainBuild } from './terrain.ts';

/**
 * The Grandsire's crown over the clouds (docs/sendas/druida.md, chapter 4), for the druid's path: boughs as wide as a
 * road, bark dark and furrowed under a thick cap of snow, open sky above, a white sea of cloud below hiding the wood,
 * the light cold and clean (the snow never cream). Nothing under the boughs: the board's tiles are hidden and the
 * pieces stand on the wood. Each place:
 *
 * - `crownBoughs` (act 1, the opening), `crownCine` (the cinematic): the boughs up the crown, the trunk beyond them
 *   going down into the cloud, the boughs above heavy with snow; between one bough and the next the cloud, and the two
 *   tips there swinging on the wind until they meet (and lock while they touch). A block coming down: a red ring
 *   closing where it lands, snow dust falling off the bough above, the block dropping its last half second; the heap it
 *   leaves.
 * - `crownHollow` (act 2, the bandage scene), `crownNight` (the campfire, an aurora over it): the hollow where boughs
 *   meet, round, its rim of bark lumps under the snow; a serpent coming out is the bark swelling (a bulge growing, cracks
 *   in the snow on it, a red ring closing), then splinters.
 * - `crownSap` (act 3, the test): the trunk coming up from below, the low crown and the high one, each fork a knob, each
 *   branch with its knot (a band of dark bark and frost round it when shut, nothing when open), each bud on its tip with
 *   its notches carved on a pale slab beside it (one to six bars, read at a glance on a phone): shut, a dark bulb rimed
 *   with frost; open, green leaves burst out of it with a glow; cracked, split with amber running. The sap: amber beads up
 *   the trunk each pulse and out along every open branch, bigger the more each carries, stopping at a shut knot. The ice
 *   plug in the trunk between the crowns, until the low one runs. The frost's touch on a knot: a pale ring and spikes.
 * - `crownTop` (the lowest point, the decision, the close): the fork under the highest bough, Olmo's fourth face carved
 *   in it; the bough Ceda walks out on, that falls with Hob through the cloud (`cue('fall')`); the green of the cure
 *   (`cue('heal')`) or the crown letting go of its snow (`cue('release')`).
 *
 * Draw calls: the dressing (one merged mesh), the clouds (two), the rings (one instanced), the heaps (one), the blocks
 * (one), the swellings (one), the beads (one), the knot bands (one), the buds (three: shut, open, cracked), the gap tips
 * (one each), the snow (one), the glow layer, the sky dome, the aurora at night (one). No lights of its own.
 */

export const COPA_PLACES = new Set(['crownCine', 'crownBoughs', 'crownHollow', 'crownNight', 'crownSap', 'crownTop']);

const S = WORLD_PER_HEX;
const K = S / 1000;
const BARK = ['#3c2e28', '#463630', '#34282a', '#4e3c32'];
// (A cold blue-grey, not white: under the day's warm key light a white snow washes out on a phone and every piece on it
// is lost; at these values the bark, the sap and the notches still read against it. docs/RENDIMIENTO.md, trampas.)
const SNOW = ['#c4d0df', '#bac7d8', '#cad5e3', '#b2c0d3'];
const ICE = '#b8d4ee';
const WARN = new THREE.Color('#ff4f3a');
const WARN2 = new THREE.Color('#ff8a6e');
const FROST = new THREE.Color('#bfe4ff');
const WHITE = new THREE.Color('#f4f8fc');
const AMBER = new THREE.Color('#ffae3c');
const GREEN = new THREE.Color('#9ee070');

type Hour = 'day' | 'dusk' | 'night' | 'dawn';

/** The light by the hour over the clouds: a clean cold day, a pink dusk, the night by the aurora. */
const HOURS: Record<Hour, { bg: string; fog: [string, number, number]; hemi: [string, string, number]; key: [string, number]; rim: [string, number]; sky: [string, string, string]; cloud: [string, string] }> = {
  day: { bg: '#a8c0dc', fog: ['#cdd8e6', 44, 170], hemi: ['#e2eaf8', '#4a5468', 1.7], key: ['#f8f4f0', 2.5], rim: ['#a8c4ff', 1.2], sky: ['#2e64a8', '#a8c8ec', '#ffffff'], cloud: ['#b4c4da', '#56667e'] },
  dawn: { bg: '#a8b4d0', fog: ['#c8cede', 32, 130], hemi: ['#fff0ec', '#56607a', 2.2], key: ['#ffe0cc', 2.3], rim: ['#b4c4ff', 1.3], sky: ['#3a5a98', '#d0b8c8', '#ffe6d4'], cloud: ['#f4eef2', '#8a8ca8'] },
  dusk: { bg: '#5a4c70', fog: ['#8c7e9c', 28, 110], hemi: ['#ffdcd4', '#2a2a3c', 2.0], key: ['#ffc4a4', 2.1], rim: ['#94a8f0', 1.5], sky: ['#1c2450', '#a07090', '#ffbe96'], cloud: ['#e8d4dc', '#5a5070'] },
  night: { bg: '#060a16', fog: ['#0c1424', 22, 90], hemi: ['#8ea4d4', '#080c16', 1.5], key: ['#bccdf4', 0.95], rim: ['#6ee0b4', 1.1], sky: ['#02040c', '#0a1a32', '#1a4a5a'], cloud: ['#4a5a78', '#0a1020'] },
};

/** The sea of cloud: soft billows drifting, lit from above (one plane, two thicknesses). */
const cloudMat = (thin: boolean): THREE.ShaderMaterial =>
  new THREE.ShaderMaterial({
    transparent: true,
    depthWrite: false,
    fog: false,
    uniforms: { time: { value: 0 }, color: { value: new THREE.Color('#f2f6fa') }, shade: { value: new THREE.Color('#8a98ac') }, open: { value: 0 } },
    vertexShader: /* glsl */ `
      varying vec3 vW;
      void main() { vec4 w = modelMatrix * vec4(position, 1.0); vW = w.xyz; gl_Position = projectionMatrix * viewMatrix * w; }`,
    fragmentShader: /* glsl */ `
      uniform float time;
      uniform vec3 color;
      uniform vec3 shade;
      uniform float open;
      varying vec3 vW;
      float h(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
      float n(vec2 p) { vec2 i = floor(p), f = fract(p); vec2 u = f * f * (3.0 - 2.0 * f);
        return mix(mix(h(i), h(i + vec2(1, 0)), u.x), mix(h(i + vec2(0, 1)), h(i + vec2(1, 1)), u.x), u.y); }
      float fbm(vec2 p) { float a = 0.5, s = 0.0; for (int i = 0; i < 4; i++) { s += a * n(p); p = p * 2.03 + 7.1; a *= 0.5; } return s; }
      void main() {
        vec2 p = vW.xz * ${thin ? '0.08' : '0.045'} + vec2(time * ${thin ? '0.04' : '0.012'}, time * 0.005);
        float f = fbm(p);
        float lit = fbm(p * 1.6 + 3.0);
        vec3 c = mix(shade, color, smoothstep(0.25, 0.85, lit) * (0.6 + 0.4 * f));
        ${thin ? 'float a = smoothstep(0.55, 0.85, f) * 0.45;' : 'float a = mix(0.97, 0.2, open * smoothstep(0.3, 0.7, n(p * 0.5)));'}
        float far = smoothstep(80.0, 140.0, length(vW.xz));
        gl_FragColor = vec4(c, a * (1.0 - far));
        #include <colorspace_fragment>
      }`,
  });

/** The aurora over the crown at night: curtains of green and teal swaying, high in the sky. */
const auroraMat = (): THREE.ShaderMaterial =>
  new THREE.ShaderMaterial({
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    side: THREE.DoubleSide,
    fog: false,
    uniforms: { time: { value: 0 } },
    vertexShader: /* glsl */ `
      varying vec2 vUv;
      void main() { vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`,
    fragmentShader: /* glsl */ `
      uniform float time;
      varying vec2 vUv;
      float h(float x) { return fract(sin(x * 91.7) * 43758.5453); }
      float n(float x) { float i = floor(x); float f = fract(x); return mix(h(i), h(i + 1.0), f * f * (3.0 - 2.0 * f)); }
      void main() {
        float x = vUv.x * 9.0;
        float wave = n(x * 0.8 + time * 0.15) * 0.6 + n(x * 2.3 - time * 0.22) * 0.4;
        float band = smoothstep(0.0, 0.25, vUv.y) * (1.0 - smoothstep(0.35 + wave * 0.5, 1.0, vUv.y));
        float rays = 0.55 + 0.45 * n(x * 7.0 + time * 0.6);
        vec3 c = mix(vec3(0.2, 0.95, 0.55), vec3(0.25, 0.7, 0.95), smoothstep(0.2, 0.9, vUv.y + wave * 0.2));
                gl_FragColor = vec4(c * band * rays * 0.95, 1.0);
      }`,
  });

/** A matrix laying a unit Y-cylinder along a segment (world points), `r` thick. */
const along = (a: THREE.Vector3, b: THREE.Vector3, rx: number, rz = rx): THREE.Matrix4 => {
  const d = new THREE.Vector3().subVectors(b, a);
  const L = Math.max(1e-4, d.length());
  const q = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), d.clone().divideScalar(L));
  return new THREE.Matrix4().compose(a.clone().add(b).multiplyScalar(0.5), q, new THREE.Vector3(rx, L, rz));
};

export class CopaLayer {
  readonly group = new THREE.Group();
  private mats: THREE.Material[] = [];
  private geos: THREE.BufferGeometry[] = [];
  private glow = new GlowLayer(220);
  private dome: SkyDome | null = null;
  private snow: Precip | null = null;
  private terrain: TerrainBuild | null = null;
  private now = 0;
  private look = new THREE.Vector3();
  private lastState: BattleState | null = null;
  private m4 = new THREE.Matrix4();
  private q4 = new THREE.Quaternion();
  private v3 = new THREE.Vector3();
  private s3 = new THREE.Vector3();
  private c3 = new THREE.Color();
  private hour: Hour = 'day';
  private cloud: THREE.ShaderMaterial | null = null;
  private wisp: THREE.ShaderMaterial | null = null;
  private aurora: THREE.ShaderMaterial | null = null;
  private lights: { p: THREE.Vector3; c: THREE.Color; size: number; a: number }[] = [];
  // Shared.
  private rings: THREE.InstancedMesh | null = null;
  private bulges: THREE.InstancedMesh | null = null;
  // Act 1.
  private Bl: BoughsLayout | null = null;
  private tips: { a: THREE.Group; b: THREE.Group; ax: number; bx: number }[] = [];
  /** The sap's beads this frame (their glow): reused points, the count drawn. */
  private beadAt: THREE.Vector3[] = [];
  private beadN = 0;
  private heaps: THREE.InstancedMesh | null = null;
  private blocks: THREE.InstancedMesh | null = null;
  // Act 2.
  private Hl: HollowLayout | null = null;
  // Act 3.
  private Sl: SapLayout | null = null;
  private nodeAt: THREE.Vector3[] = [];
  private knotAt: THREE.Vector3[] = [];
  private bands: THREE.InstancedMesh | null = null;
  private budsShut: THREE.InstancedMesh | null = null;
  private budsOpen: THREE.InstancedMesh | null = null;
  private budsCrack: THREE.InstancedMesh | null = null;
  private beads: THREE.InstancedMesh | null = null;
  private plug: THREE.Mesh | null = null;
  private plugMelt = 0;
  private pulseAt = -1;
  private runFlash = 0;
  // The top.
  private fallBough: THREE.Group | null = null;
  private fallT = -1;
  private knife: THREE.Mesh | null = null;
  private healT = -1;
  private releaseT = -1;
  private burst: Precip | null = null;

  constructor(
    private ctx: OmenCtx,
    private map: MapDef,
    readonly place: string,
    s: BattleState,
  ) {
    this.group.name = `copa-${place}`;
    const kit = new Kit();
    this.shared();
    const seed = s.seed;
    if (place === 'crownBoughs' || place === 'crownCine') this.buildBoughs(kit, boughsLayout(seed), place === 'crownCine');
    else if (place === 'crownHollow' || place === 'crownNight') this.buildHollow(kit, hollowLayout(seed), place === 'crownNight');
    else if (place === 'crownSap') this.buildSap(kit, sapLayout(seed, DRUID_PATH.sap.need));
    else this.buildTop(kit);
    const mat = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.9, metalness: 0.02, flatShading: true });
    this.mats.push(mat);
    const dress = kit.build(mat, 'copa-dress');
    if (dress) {
      dress.castShadow = true;
      dress.receiveShadow = true;
      this.geos.push(dress.geometry);
      this.group.add(dress);
    }
    // Snow falling, fine and slow, drifting on the wind.
    this.snow = new Precip({ count: ctx.low ? 70 : 150, box: [14, 9, 14], fall: 0.7, wind: [0.6, 0.15], sway: 0.6, size: [0.05, 0.05], color: '#f4f8fc', opacity: 0.85, round: true }, 71);
    this.group.add(this.snow.mesh);
    this.group.add(this.glow.mesh);
  }

  /* ---------- helpers ---------- */

  private track<T extends THREE.Material | THREE.BufferGeometry>(x: T): T {
    if (x instanceof THREE.Material) this.mats.push(x);
    else this.geos.push(x);
    return x;
  }

  /** A sim point to a world point at height `h` (the boughs' snow is at 0). */
  private pt(x: number, y: number, h = 0): THREE.Vector3 {
    return new THREE.Vector3(x * K, h, y * K);
  }

  private hexPt(q: number, r: number, h = 0): THREE.Vector3 {
    const c = hexToWorld(q, r);
    return this.pt(c.x, c.y, h);
  }

  private inst(geo: THREE.BufferGeometry, mat: THREE.Material, n: number, name: string, order = 0): THREE.InstancedMesh {
    const m = new THREE.InstancedMesh(geo, mat, n);
    m.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(n * 3), 3);
    m.count = 0;
    m.frustumCulled = false;
    m.name = name;
    if (order) m.renderOrder = order;
    this.group.add(m);
    return m;
  }

  private shared(): void {
    const rg = this.track(new THREE.RingGeometry(0.86, 1, 40).rotateX(-Math.PI / 2));
    const rm = this.track(new THREE.MeshBasicMaterial({ color: '#ffffff', transparent: true, opacity: 0.92, depthWrite: false, side: THREE.DoubleSide }));
    this.rings = this.inst(rg, rm, 28, 'copa-rings', 5);
    // A serpent swelling the bark: a dome of bark with snow cracking off it.
    const bg = this.track(new THREE.SphereGeometry(0.5, 10, 6, 0, Math.PI * 2, 0, Math.PI / 2));
    const bm = this.track(new THREE.MeshStandardMaterial({ color: '#ffffff', roughness: 0.95, flatShading: true }));
    this.bulges = this.inst(bg, bm, 8, 'copa-bulges');
    // The sea of cloud below, and a thin veil drifting nearer.
    const cg = this.track(new THREE.PlaneGeometry(300, 300).rotateX(-Math.PI / 2));
    this.cloud = this.track(cloudMat(false));
    const cm = new THREE.Mesh(cg, this.cloud);
    cm.position.y = -4.2;
    cm.renderOrder = -2;
    cm.name = 'copa-cloud';
    this.wisp = this.track(cloudMat(true));
    const wm = new THREE.Mesh(cg, this.wisp);
    wm.position.y = -2.6;
    wm.renderOrder = -1;
    wm.name = 'copa-wisp';
    this.group.add(cm, wm);
  }

  /** A bough: a round log of dark furrowed bark laid along a segment just under the snow, its snow flat on top. */
  private bough(kit: Kit, b: Bough, seed: number, top = 0, sap = false): void {
    const cyl = new THREE.CylinderGeometry(1, 1, 1, 10);
    const box = new THREE.BoxGeometry(1, 1, 1);
    const sph = new THREE.SphereGeometry(1, 8, 5);
    const r = b.w * K * 0.9;
    const a = this.pt(b.a.x, b.a.y, top - r - 0.05);
    const c = this.pt(b.b.x, b.b.y, top - r - 0.05);
    kit.add(cyl, BARK[seed % BARK.length] as string, along(a, c, r));
    const d = new THREE.Vector3().subVectors(c, a);
    const L = d.length();
    const yaw = -Math.atan2(d.z, d.x);
    const dir = d.clone().normalize();
    const side = new THREE.Vector3(-dir.z, 0, dir.x);
    // Furrows of paler bark down its flanks.
    const nf = Math.max(1, Math.floor(L / 0.8));
    for (let i = 0; i < nf; i++) {
      const p = a.clone().addScaledVector(dir, ((i + 0.5) / nf) * L);
      for (const sgn of [-1, 1]) {
        const q = p.clone().addScaledVector(side, sgn * r * 0.97).setY(p.y + (((i * 7 + seed) % 3) - 1) * 0.12 * r);
        kit.add(box, BARK[(seed + i + 1) % BARK.length] as string, new THREE.Matrix4().compose(q, new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), yaw), new THREE.Vector3(0.42, 0.06, 0.05)));
      }
    }
    // The snow: a flat slab where they walk, soft lumps along both edges, a little blue in its shadow underneath.
    const mid = a.clone().add(c).multiplyScalar(0.5).setY(top - 0.07);
    const rot = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), yaw);
    kit.add(box, SNOW[seed % SNOW.length] as string, new THREE.Matrix4().compose(mid, rot, new THREE.Vector3(L, 0.14, r * 1.36)));
    kit.add(box, '#b8c8dc', new THREE.Matrix4().compose(mid.clone().setY(top - 0.16), rot, new THREE.Vector3(L * 0.98, 0.07, r * 1.5)));
    // Tracks trodden through it, a shade bluer, down the middle; on a branch the sap runs up, the snow is swept off a
    // strip of bare dark bark instead, a groove where the amber beads go (it reads on a phone against the snow).
    if (sap) {
      kit.add(box, '#120c0a', new THREE.Matrix4().compose(mid.clone().setY(top + 0.004), rot, new THREE.Vector3(L * 0.98, 0.02, r * 0.78)));
      kit.add(box, '#3a2620', new THREE.Matrix4().compose(mid.clone().setY(top + 0.008), rot, new THREE.Vector3(L * 0.97, 0.02, r * 0.56)));
      kit.add(box, '#4e2c18', new THREE.Matrix4().compose(mid.clone().setY(top + 0.012), rot, new THREE.Vector3(L * 0.96, 0.02, r * 0.16)));
    } else kit.add(box, '#a8b8cc', new THREE.Matrix4().compose(mid.clone().setY(top - 0.002), rot, new THREE.Vector3(L * 0.96, 0.02, r * 0.42)));
    const nl = Math.max(2, Math.floor(L / 0.55));
    for (let i = 0; i <= nl; i++) {
      const p = a.clone().addScaledVector(dir, (i / nl) * L);
      for (const sgn of sap ? [] : [-1, 1]) {
        const k2 = ((i * 13 + seed * 7 + (sgn > 0 ? 3 : 0)) % 5) / 5;
        if ((i + (sgn > 0 ? 1 : 0)) % 2) continue;
        kit.add(sph, SNOW[(i + seed) % SNOW.length] as string, new THREE.Matrix4().compose(p.clone().addScaledVector(side, sgn * r * 0.66).setY(top - 0.08), new THREE.Quaternion(), new THREE.Vector3(0.22 + k2 * 0.1, 0.11 + k2 * 0.05, 0.18 + k2 * 0.08)));
      }
    }
    // Icicles hanging off its underside.
    const cone = new THREE.ConeGeometry(0.05, 1, 4);
    for (let i = 0; i < Math.floor(L / 0.7); i++) {
      const k = ((i * 37 + seed * 13) % 100) / 100;
      const p = a.clone().addScaledVector(dir, k * L).addScaledVector(side, (((i * 17) % 7) - 3) * 0.1 * r);
      const len = 0.25 + ((i * 29 + seed) % 5) * 0.12;
      kit.add(cone, ICE, new THREE.Matrix4().compose(p.clone().setY(p.y - r * 0.95 - len * 0.5), new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(1, 0, 0), Math.PI), new THREE.Vector3(1, len, 1)));
    }
    cone.dispose();
    cyl.dispose();
    box.dispose();
    sph.dispose();
  }

  /** A pad: where boughs meet or a bud sits, a knob of bark and its round of snow. */
  private pad(kit: Kit, p: Pad, seed: number, top = 0): void {
    const cyl = new THREE.CylinderGeometry(1, 1, 1, 12);
    const r = p.r * K * 0.82;
    const c = this.pt(p.p.x, p.p.y, top);
    kit.add(cyl, BARK[(seed + 2) % BARK.length] as string, new THREE.Matrix4().compose(c.clone().setY(top - r * 0.55), new THREE.Quaternion(), new THREE.Vector3(r, r * 1.1, r)));
    kit.add(cyl, SNOW[(seed + 1) % SNOW.length] as string, new THREE.Matrix4().compose(c.clone().setY(top - 0.06), new THREE.Quaternion(), new THREE.Vector3(r * 1.04, 0.14, r * 1.04)));
    cyl.dispose();
  }

  /** The great trunk going down into the cloud, and the boughs above heavy with snow (the dressing round a place). */
  private trunk(kit: Kit, x: number, z: number, r: number, top: number): void {
    const cyl = new THREE.CylinderGeometry(0.82, 1, 1, 14);
    kit.add(cyl, BARK[1] as string, new THREE.Matrix4().compose(new THREE.Vector3(x, (top - 30) / 2, z), new THREE.Quaternion(), new THREE.Vector3(r, top + 30, r)));
    // Ridges of bark down it.
    for (let i = 0; i < 12; i++) {
      const a = (i / 12) * Math.PI * 2;
      kit.add(cyl, BARK[(i + 2) % BARK.length] as string, new THREE.Matrix4().compose(new THREE.Vector3(x + Math.cos(a) * r * 0.95, (top - 30) / 2, z + Math.sin(a) * r * 0.95), new THREE.Quaternion(), new THREE.Vector3(r * 0.14, top + 30, r * 0.14)));
    }
    // Snow caught on its ledges.
    for (let i = 0; i < 6; i++) {
      const a = i * 1.9;
      kit.add(new THREE.SphereGeometry(1, 8, 5), SNOW[i % SNOW.length] as string, new THREE.Matrix4().compose(new THREE.Vector3(x + Math.cos(a) * r * 0.9, top - 2 - i * 2.4, z + Math.sin(a) * r * 0.9), new THREE.Quaternion(), new THREE.Vector3(r * 0.4, 0.22, r * 0.3)));
    }
    cyl.dispose();
  }

  /* ---------- act 1: the boughs ---------- */

  private buildBoughs(kit: Kit, L: BoughsLayout, cine: boolean): void {
    this.Bl = L;
    L.boughs.forEach((b, i) => this.bough(kit, b, i));
    L.pads.forEach((p, i) => this.pad(kit, p, i));
    // The trunk the boughs come off, beyond the top; another far off under the cloud's edge.
    const t = this.pt(L.top.x, L.top.y);
    this.trunk(kit, t.x - 3.6, t.z - 5.5, 3.2, 9);
    this.trunk(kit, -18, -6, 2.2, 4);
    // The gaps' tips: each side's end of bough, its own group, swinging on the wind.
    const cyl = this.track(new THREE.CylinderGeometry(1, 1, 1, 8));
    const box = this.track(new THREE.BoxGeometry(1, 1, 1));
    const bark = this.track(new THREE.MeshStandardMaterial({ color: BARK[0], roughness: 0.95, flatShading: true }));
    const snow = this.track(new THREE.MeshStandardMaterial({ color: SNOW[0], roughness: 0.9, flatShading: true }));
    const edge = this.track(new THREE.MeshStandardMaterial({ color: '#1a1210', roughness: 0.95, flatShading: true }));
    const lip = new THREE.CylinderGeometry(1, 1, 1, 16);
    for (const g of L.gaps) {
      // Each end at a gap: a black rim of cut bark round its pad's snow (the pad's own radius, as pad() lays it), so
      // where the wood stops over the cloud reads at a glance even while the tips are swung away.
      for (const [p, pr] of [
        [g.a, 900],
        [g.b, 850],
      ] as const) {
        const c = this.pt(p.x, p.y);
        const r = pr * K * 0.82;
        kit.add(lip, '#120c0a', new THREE.Matrix4().compose(c.clone().setY(-0.058), new THREE.Quaternion(), new THREE.Vector3(r * 1.22, 0.1, r * 1.22)));
      }
      const a = this.pt(g.a.x, g.a.y);
      const b = this.pt(g.b.x, g.b.y);
      const mid = a.clone().add(b).multiplyScalar(0.5);
      const mk = (from: THREE.Vector3): THREE.Group => {
        const grp = new THREE.Group();
        grp.position.copy(from);
        const to = mid.clone().sub(from);
        const len = to.length() * 1.08;
        const dir = to.clone().normalize();
        // A tip of bough: a log thinning out over the cloud, its snow on top, a twig or two off it.
        const m1 = new THREE.Mesh(cyl, bark);
        m1.applyMatrix4(along(new THREE.Vector3(0, -0.66, 0), dir.clone().multiplyScalar(len).setY(-0.62), 0.6));
        const m2 = new THREE.Mesh(box, snow);
        m2.applyMatrix4(new THREE.Matrix4().compose(dir.clone().multiplyScalar(len * 0.44).setY(-0.07), new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), -Math.atan2(dir.z, dir.x)), new THREE.Vector3(len * 0.88, 0.14, 0.82)));
        // Its end bare, dark bark past the snow: where the bough stops over the cloud reads at a glance.
        const m3 = new THREE.Mesh(box, edge);
        m3.applyMatrix4(new THREE.Matrix4().compose(dir.clone().multiplyScalar(len * 0.94).setY(-0.06), new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), -Math.atan2(dir.z, dir.x)), new THREE.Vector3(len * 0.12, 0.16, 0.9)));
        const tw = new THREE.Mesh(cyl, bark);
        const side = new THREE.Vector3(-dir.z, 0, dir.x);
        tw.applyMatrix4(along(dir.clone().multiplyScalar(len * 0.6).setY(-0.5), dir.clone().multiplyScalar(len * 0.9).addScaledVector(side, 0.9).setY(-0.1), 0.09));
        m1.castShadow = true;
        grp.add(m1, m2, m3, tw);
        this.group.add(grp);
        return grp;
      };
      this.tips.push({ a: mk(a), b: mk(b), ax: 0, bx: 0 });
    }
    lip.dispose();
    const hg = this.track(new THREE.SphereGeometry(0.5, 10, 6, 0, Math.PI * 2, 0, Math.PI / 2));
    const hm = this.track(new THREE.MeshStandardMaterial({ color: '#f2f6fa', roughness: 0.95, flatShading: true }));
    this.heaps = this.inst(hg, hm, 16, 'copa-heaps');
    const blg = this.track(new THREE.DodecahedronGeometry(0.6, 0));
    this.blocks = this.inst(blg, hm, 8, 'copa-blocks');
    void cine;
  }

  private drawBoughs(s: BattleState): void {
    const w = s.senda?.winter;
    const r = s.rules?.senda?.winter?.boughs;
    if (!w || !r) return;
    // The heaps.
    const hp = this.heaps;
    if (hp) {
      let n = 0;
      for (const p of w.heaps) {
        if (n >= hp.instanceMatrix.count) break;
        const left = Math.max(0, Math.min(1, ((p[2] as number) - s.tick) / Math.max(1, r.snow.heap)));
        const rad = (r.snow.heapR / 1000) * S * (0.6 + 0.4 * Math.min(1, left * 3));
        this.m4.compose(this.pt(p[0] as number, p[1] as number, -0.04), this.q4.identity(), this.s3.set(rad * 2, 0.5 * Math.min(1, left * 3), rad * 2));
        hp.setMatrixAt(n, this.m4);
        hp.setColorAt(n++, WHITE);
      }
      hp.count = n;
      hp.instanceMatrix.needsUpdate = true;
      if (hp.instanceColor) hp.instanceColor.needsUpdate = true;
    }
  }

  /* ---------- act 2: the hollow ---------- */

  private buildHollow(kit: Kit, L: HollowLayout, night: boolean): void {
    this.Hl = L;
    L.boughs.forEach((b, i) => this.bough(kit, b, i));
    const cyl = new THREE.CylinderGeometry(1, 1, 1, 18);
    const sph = new THREE.SphereGeometry(1, 9, 6);
    const r = (L.pads[0]?.r ?? 4300) * K;
    // The hollow's floor: old wood, rings of it, snow drifted to its rim.
    kit.add(cyl, '#4a3a32', new THREE.Matrix4().compose(new THREE.Vector3(0, -0.9, 0), new THREE.Quaternion(), new THREE.Vector3(r * 1.05, 1.8, r * 1.05)));
    kit.add(cyl, '#6a5446', new THREE.Matrix4().compose(new THREE.Vector3(0, -0.02, 0), new THREE.Quaternion(), new THREE.Vector3(r * 0.9, 0.04, r * 0.9)));
    for (let i = 1; i <= 4; i++) kit.add(cyl, i % 2 ? '#5c483c' : '#725a4a', new THREE.Matrix4().compose(new THREE.Vector3(0, 0.0 + i * 0.002, 0), new THREE.Quaternion(), new THREE.Vector3(r * (0.9 - i * 0.18), 0.04, r * (0.9 - i * 0.18))));
    // The rim: lumps of bark under the snow all round, gaps where the boughs go off.
    const out = L.boughs.map((b) => Math.atan2(b.b.y - b.a.y, b.b.x - b.a.x));
    for (let i = 0; i < 26; i++) {
      const a = (i / 26) * Math.PI * 2;
      if (out.some((o) => Math.abs(Math.atan2(Math.sin(a - o), Math.cos(a - o))) < 0.32)) continue;
      const rr = r * (1.0 + ((i * 7) % 3) * 0.04);
      const x = Math.cos(a) * rr;
      const z = Math.sin(a) * rr;
      const hh = 0.5 + ((i * 13) % 5) * 0.16;
      kit.add(sph, BARK[i % BARK.length] as string, new THREE.Matrix4().compose(new THREE.Vector3(x, hh * 0.4, z), new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), -a), new THREE.Vector3(0.7, hh, 0.55)));
      kit.add(sph, SNOW[i % SNOW.length] as string, new THREE.Matrix4().compose(new THREE.Vector3(x * 0.98, hh * 0.85, z * 0.98), new THREE.Quaternion(), new THREE.Vector3(0.62, 0.2, 0.5)));
      kit.add(sph, SNOW[(i + 1) % SNOW.length] as string, new THREE.Matrix4().compose(new THREE.Vector3(x * 0.9, 0.04, z * 0.9), new THREE.Quaternion(), new THREE.Vector3(0.8, 0.12, 0.6)));
    }
    // The trunk rising behind the hollow, its top lost above.
    this.trunk(kit, -1.2, -r - 3.4, 2.8, 14);
    // Ceda's satchel, a coil of rope, Hob's stick (the scene's own things).
    kit.add(new THREE.BoxGeometry(1, 1, 1), '#5a4a3a', new THREE.Matrix4().compose(this.hexPt(0, 1, 0.12), new THREE.Quaternion(), new THREE.Vector3(0.3, 0.22, 0.2)));
    kit.add(cyl, '#8a7a62', new THREE.Matrix4().compose(this.hexPt(-2, 1, 0.05), new THREE.Quaternion(), new THREE.Vector3(0.22, 0.08, 0.22)));
    kit.add(cyl, '#6a5040', along(this.hexPt(-2, 0, 0.06), this.hexPt(-1, -1, 0.06), 0.035));
    if (night) {
      this.aurora = this.track(auroraMat());
      const ag = this.track(new THREE.CylinderGeometry(46, 46, 24, 64, 1, true));
      const am = new THREE.Mesh(ag, this.aurora);
      am.position.set(0, 9, 0);
      am.renderOrder = 2;
      am.frustumCulled = false;
      am.name = 'copa-aurora';
      this.group.add(am);
    }
    cyl.dispose();
    sph.dispose();
  }

  /* ---------- act 3: the sap of the crown ---------- */

  private buildSap(kit: Kit, L: SapLayout): void {
    this.Sl = L;
    // (Every bough but Hob's hollow's last carries sap: its groove of bare bark.)
    L.boughs.forEach((b, i) => this.bough(kit, b, i, 0, i < L.boughs.length - 1));
    L.pads.forEach((p, i) => this.pad(kit, p, i));
    this.nodeAt = L.nodes.map((n) => this.pt(n.x, n.y));
    this.knotAt = L.knots.map((k) => this.pt(k.x, k.y));
    // The trunk's foot going down into the cloud.
    const foot = this.pt(L.foot.x, L.foot.y);
    this.trunk(kit, foot.x, foot.z + 1.2, 1.6, -0.2);
    // Hob's hollow off the foot: its rim.
    const sph = new THREE.SphereGeometry(1, 8, 5);
    const ho = this.pt(L.hollow.x, L.hollow.y);
    for (let i = 0; i < 10; i++) {
      const a = (i / 10) * Math.PI * 2;
      if (Math.abs(Math.atan2(Math.sin(a - 0.3), Math.cos(a - 0.3))) < 0.5) continue;
      kit.add(sph, BARK[i % BARK.length] as string, new THREE.Matrix4().compose(new THREE.Vector3(ho.x + Math.cos(a) * 1.6, 0.18, ho.z + Math.sin(a) * 1.6), new THREE.Quaternion(), new THREE.Vector3(0.45, 0.45, 0.4)));
    }
    // Each bud's notches: a dark slab of bark beside it in a black frame, its bars pale and raised, standing to face the
    // camera (a pale slab on the snow was lost on a phone).
    const box = new THREE.BoxGeometry(1, 1, 1);
    L.nodes.forEach((n) => {
      if (!n.need) return;
      const p = this.pt(n.x, n.y);
      const sx = p.x + (n.x >= 0 ? 0.95 : -0.95);
      const sz = p.z + 0.55;
      const wd = 0.32 + n.need * 0.17;
      const tilt = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(1, 0, 0), -0.35);
      kit.add(box, '#0a0809', new THREE.Matrix4().compose(new THREE.Vector3(sx, 0.42, sz - 0.03), tilt, new THREE.Vector3(wd + 0.16, 0.8, 0.08)));
      kit.add(box, '#2e2226', new THREE.Matrix4().compose(new THREE.Vector3(sx, 0.42, sz), tilt, new THREE.Vector3(wd, 0.66, 0.1)));
      for (let k = 0; k < n.need; k++) {
        const bx = sx - (wd - 0.3) / 2 + k * 0.17;
        kit.add(box, '#e4ecf6', new THREE.Matrix4().compose(new THREE.Vector3(bx, 0.44, sz + 0.06), tilt, new THREE.Vector3(0.09, 0.46, 0.06)));
      }
    });
    box.dispose();
    sph.dispose();
    // The ice in the trunk between the crowns.
    const pg = this.track(new THREE.CylinderGeometry(0.75, 0.75, 1.4, 10));
    const pm = this.track(new THREE.MeshStandardMaterial({ color: '#cfe6fb', roughness: 0.2, metalness: 0.05, transparent: true, opacity: 0.78, emissive: '#5a88b8', emissiveIntensity: 0.25 }));
    this.plug = new THREE.Mesh(pg, pm);
    this.plug.position.copy(this.pt(L.plug.x, L.plug.y, 0.3));
    this.plug.name = 'copa-plug';
    this.group.add(this.plug);
    // The knots' bands (shut), the buds (shut, open, cracked), the beads of sap.
    const tg = this.track(new THREE.TorusGeometry(0.42, 0.13, 6, 14));
    const tm = this.track(new THREE.MeshStandardMaterial({ color: '#ffffff', roughness: 0.6, flatShading: true }));
    this.bands = this.inst(tg, tm, Math.max(1, L.knots.length), 'copa-bands');
    const bud = this.track(new THREE.SphereGeometry(0.36, 10, 7));
    const shutM = this.track(new THREE.MeshStandardMaterial({ color: '#ffffff', roughness: 0.7, flatShading: true }));
    this.budsShut = this.inst(bud, shutM, 12, 'copa-buds-shut');
    const leaf = this.track(new THREE.IcosahedronGeometry(0.55, 0));
    const openM = this.track(new THREE.MeshStandardMaterial({ color: '#ffffff', roughness: 0.6, flatShading: true, emissive: '#3a7a20', emissiveIntensity: 0.5 }));
    this.budsOpen = this.inst(leaf, openM, 12, 'copa-buds-open');
    const crack = this.track(new THREE.OctahedronGeometry(0.42, 0));
    const crackM = this.track(new THREE.MeshStandardMaterial({ color: '#ffffff', roughness: 0.5, flatShading: true, emissive: '#a05a10', emissiveIntensity: 0.6 }));
    this.budsCrack = this.inst(crack, crackM, 12, 'copa-buds-crack');
    const bg = this.track(new THREE.SphereGeometry(0.2, 8, 6));
    const bm = this.track(new THREE.MeshBasicMaterial({ color: '#ffffff' }));
    this.beads = this.inst(bg, bm, 90, 'copa-beads', 3);
  }

  /** The way a bead of sap goes from the trunk's foot to a node (world points). */
  private pathTo(i: number): THREE.Vector3[] {
    const L = this.Sl;
    if (!L) return [];
    const out: THREE.Vector3[] = [];
    for (let c = i; c >= 0; c = (L.nodes[c] as { parent: number }).parent) out.unshift(this.nodeAt[c] as THREE.Vector3);
    const root = out[0];
    const foot = this.pt(L.foot.x, L.foot.y);
    // The high crown's sap comes up past the low crown's fork.
    if (root && (L.nodes[i] as { stage: number }).stage === 1) out.unshift(this.nodeAt[L.roots[0] as number] as THREE.Vector3);
    out.unshift(foot);
    return out;
  }

  private drawSap(s: BattleState, now: number): void {
    const L = this.Sl;
    const w = s.senda?.winter;
    const r = s.rules?.senda?.winter?.sap;
    if (!L || !w || !r || !w.closed.length) return;
    // The knots' bands: dark bark and frost round a shut one (the frost's touch blue on it).
    const bands = this.bands;
    if (bands) {
      let n = 0;
      // Every knot: open, a loose ring of pale wood leaning on the branch; shut, a band of dark bark clenched round it.
      L.knots.forEach((k, i) => {
        const node = L.nodes[k.node] as { x: number; y: number; parent: number; stage: number };
        const par = L.nodes[node.parent] as { x: number; y: number };
        const yaw = Math.atan2(node.y - par.y, node.x - par.x);
        const p = this.knotAt[i] as THREE.Vector3;
        const shut = w.closed[i] === 1;
        const active = node.stage === w.stage;
        // Shut: a thick black band clenched round the branch across the groove (the sap stops at it). Open: the band
        // undone, lying flat on the snow off to the side, thin and mid-brown, the groove bare and running past it.
        if (shut) {
          this.q4.setFromEuler(new THREE.Euler(0, -yaw + Math.PI / 2, 0));
          this.m4.compose(this.v3.set(p.x, -0.02, p.z), this.q4, this.s3.set(2.0, 2.0, 2.8));
        } else {
          const sx = -Math.sin(yaw) * 0.75;
          const sz = Math.cos(yaw) * 0.75;
          this.q4.setFromEuler(new THREE.Euler(Math.PI / 2, 0, yaw));
          this.m4.compose(this.v3.set(p.x + sx, 0.08, p.z + sz), this.q4, this.s3.set(0.9, 0.9, 0.5));
        }
        bands.setMatrixAt(n, this.m4);
        bands.setColorAt(n++, this.c3.set(w.bknot === i && w.bwarn >= 0 ? '#bfe4ff' : shut ? (active ? '#0c0808' : '#3a3436') : active ? '#7a5038' : '#5c5658'));
      });
      bands.count = n;
      bands.instanceMatrix.needsUpdate = true;
      if (bands.instanceColor) bands.instanceColor.needsUpdate = true;
    }
    // The buds.
    let a = 0;
    let b = 0;
    let c = 0;
    const set = (m: THREE.InstancedMesh | null, i: number, p: THREE.Vector3, sc: number, col: THREE.Color, spin = 0): void => {
      if (!m) return;
      this.q4.setFromEuler(new THREE.Euler(0.4, spin, 0.2));
      this.m4.compose(this.v3.set(p.x, p.y + 0.45, p.z), this.q4, this.s3.set(sc, sc, sc));
      m.setMatrixAt(i, this.m4);
      m.setColorAt(i, col);
    };
    L.nodes.forEach((n, i) => {
      if (!n.need) return;
      const p = this.nodeAt[i] as THREE.Vector3;
      const st = w.buds[i] ?? -1;
      const done = n.stage < w.stage || w.done >= 0;
      if (st === 0 || done) set(this.budsOpen, a++, p, 1.0 + 0.06 * Math.sin(now * 3 + i), this.c3.set(n.stage < w.stage ? '#a8d888' : '#8fdc5a'), now * 0.3 + i);
      else if (st === 1) set(this.budsCrack, b++, p, 1.0, this.c3.set('#c87a34'), 0.4);
      else set(this.budsShut, c++, p, 1.0, this.c3.set(n.stage === w.stage ? '#3e4a3a' : '#5a6070'));
    });
    for (const [m, k] of [[this.budsOpen, a], [this.budsCrack, b], [this.budsShut, c]] as [THREE.InstancedMesh | null, number][]) {
      if (!m) continue;
      m.count = k;
      m.instanceMatrix.needsUpdate = true;
      if (m.instanceColor) m.instanceColor.needsUpdate = true;
    }
    // The beads: each pulse up the trunk and out along the open branches, as big as what each carries.
    const beads = this.beads;
    if (beads && w.done < 0) {
      const flow = sapFlow(L.nodes, L.knots, L.roots[w.stage] as number, w.closed.map((x) => x === 1));
      const since = (s.tick - (w.pulseNext - r.pulse) + this.frac()) / Math.max(1, r.pulse);
      const shut = new Set<number>();
      L.knots.forEach((k, i) => w.closed[i] && shut.add(k.node));
      let n = 0;
      L.nodes.forEach((node, i) => {
        if (!node.need || node.stage !== w.stage) return;
        // Its share: 12 over the splits on its way (to the first shut knot, where it stops).
        const way = this.pathTo(i);
        let stop = way.length - 1;
        let share = 12;
        for (let c2 = i, depth = 0; c2 >= 0 && depth < 8; depth++) {
          const nd = L.nodes[c2] as { parent: number };
          if (shut.has(c2)) stop = Math.min(stop, way.length - 1 - depth);
          c2 = nd.parent;
        }
        for (let c2 = (L.nodes[i] as { parent: number }).parent; c2 >= 0; c2 = (L.nodes[c2] as { parent: number }).parent) {
          const open = (L.nodes[c2] as { kids: number[] }).kids.filter((k) => !shut.has(k)).length;
          share /= Math.max(1, open);
        }
        const f = flow[i] as number;
        // Each of its stretches, a bead running up it in turn.
        const legs = Math.max(1, stop);
        const t = Math.min(0.999, since) * legs;
        const leg = Math.floor(t);
        const k = t - leg;
        const pa = way[leg];
        const pb = way[Math.min(way.length - 1, leg + 1)];
        if (!pa || !pb || n >= beads.instanceMatrix.count) return;
        this.v3.lerpVectors(pa, pb, k).setY(0.22);
        const sc = 0.6 + Math.min(1.6, share / 3);
        this.m4.compose(this.v3, this.q4.identity(), this.s3.set(sc, sc, sc));
        beads.setMatrixAt(n, this.m4);
        (this.beadAt[n] ??= new THREE.Vector3()).copy(this.v3);
        beads.setColorAt(n++, f === 1 ? WARN2 : AMBER);
      });
      beads.count = n;
      this.beadN = n;
      beads.instanceMatrix.needsUpdate = true;
      if (beads.instanceColor) beads.instanceColor.needsUpdate = true;
    } else if (beads) beads.count = this.beadN = 0;
  }

  /* ---------- the top ---------- */

  private buildTop(kit: Kit): void {
    // The fork under the highest bough: a wide platform of old wood.
    this.pad(kit, { p: { x: 0, y: 0 }, r: 3200 }, 1);
    this.bough(kit, { a: { x: -500, y: 300 }, b: { x: -6500, y: 4200 }, w: 1000 }, 2);
    this.bough(kit, { a: { x: 0, y: 0 }, b: { x: -2600, y: -6200 }, w: 900 }, 3);
    // The highest bough rising, Olmo's fourth face carved in it, the words under it.
    const cyl = new THREE.CylinderGeometry(1, 1, 1, 10);
    const base = this.hexPt(-2, -2, 0);
    kit.add(cyl, BARK[0] as string, along(base.clone().setY(-1), base.clone().add(new THREE.Vector3(-0.6, 7, -1.2)), 0.9));
    kit.add(cyl, SNOW[0] as string, along(base.clone().add(new THREE.Vector3(-0.3, 3.5, -0.3)), base.clone().add(new THREE.Vector3(-0.6, 7.2, -1.0)), 0.32, 0.12));
    this.face(kit, base.clone().add(new THREE.Vector3(0.05, 2.0, 0.75)), 1.0);
    // The trunk below and behind, into the cloud.
    this.trunk(kit, -4.4, -3.8, 2.6, 0);
    // The bough Ceda walks out on, that falls: its own group.
    const grp = new THREE.Group();
    const bark = this.track(new THREE.MeshStandardMaterial({ color: BARK[2], roughness: 0.95, flatShading: true }));
    const snow = this.track(new THREE.MeshStandardMaterial({ color: SNOW[1], roughness: 0.9, flatShading: true }));
    const g = this.track(new THREE.CylinderGeometry(1, 1, 1, 9));
    const from = this.hexPt(1, 0, 0);
    const to = this.hexPt(7, 2, 0);
    const m1 = new THREE.Mesh(g, bark);
    m1.applyMatrix4(along(new THREE.Vector3(0, -0.8, 0), to.clone().sub(from).setY(-0.8), 0.78));
    const dv = to.clone().sub(from);
    const m2 = new THREE.Mesh(this.track(new THREE.BoxGeometry(1, 1, 1)), snow);
    m2.applyMatrix4(new THREE.Matrix4().compose(dv.clone().multiplyScalar(0.5).setY(-0.07), new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), -Math.atan2(dv.z, dv.x)), new THREE.Vector3(dv.length(), 0.14, 1.3)));
    m1.castShadow = true;
    grp.add(m1, m2);
    grp.position.copy(from);
    this.group.add(grp);
    this.fallBough = grp;
    // Espino's curved knife (it falls too).
    const kg = this.track(new THREE.TorusGeometry(0.16, 0.025, 4, 10, Math.PI * 0.9));
    const km = this.track(new THREE.MeshStandardMaterial({ color: '#c8ccd4', roughness: 0.3, metalness: 0.8 }));
    this.knife = new THREE.Mesh(kg, km);
    this.knife.position.copy(this.hexPt(-1, 0, 0.9));
    this.knife.visible = false;
    this.group.add(this.knife);
    this.burst = new Precip({ count: this.ctx.low ? 160 : 360, box: [10, 9, 10], fall: 2.4, wind: [0.2, 0.1], sway: 0.4, size: [0.09, 0.09], color: '#f6f9fc', opacity: 0, round: true }, 73);
    this.group.add(this.burst.mesh);
    cyl.dispose();
  }

  /** Olmo's face carved in bark, looking south: the oval smoothed pale, the eyes, the mouth, the words in two lines under it. */
  private face(kit: Kit, p: THREE.Vector3, size: number): void {
    const sph = new THREE.SphereGeometry(0.42, 12, 8);
    const box = new THREE.BoxGeometry(1, 1, 1);
    const cut = '#2a1a12';
    kit.add(sph, '#9c8470', new THREE.Matrix4().compose(p, new THREE.Quaternion(), new THREE.Vector3(1.4 * size, 1.9 * size, 0.28 * size)));
    for (const k of [-1, 1]) kit.add(sph, cut, new THREE.Matrix4().compose(p.clone().add(new THREE.Vector3(k * 0.18 * size, 0.16 * size, 0.1 * size)), new THREE.Quaternion(), new THREE.Vector3(0.22 * size, 0.09 * size, 0.08 * size)));
    kit.add(box, cut, new THREE.Matrix4().compose(p.clone().add(new THREE.Vector3(0, -0.28 * size, 0.11 * size)), new THREE.Quaternion(), new THREE.Vector3(0.3 * size, 0.04 * size, 0.05 * size)));
    kit.add(box, '#8a7262', new THREE.Matrix4().compose(p.clone().add(new THREE.Vector3(0, -0.02 * size, 0.12 * size)), new THREE.Quaternion(), new THREE.Vector3(0.07 * size, 0.24 * size, 0.07 * size)));
    for (let i = 0; i < 2; i++) kit.add(box, cut, new THREE.Matrix4().compose(p.clone().add(new THREE.Vector3(0, -(0.6 + i * 0.12) * size, 0.06 * size)), new THREE.Quaternion(), new THREE.Vector3((0.5 - i * 0.14) * size, 0.03 * size, 0.04 * size)));
    sph.dispose();
    box.dispose();
  }

  /** A moment of the top's scenes: the bough falls with Hob (`fall`), the sap goes green (`heal`), the snow lets go (`release`). */
  cue(c: string): void {
    if (c === 'cut') this.ctx.shake(0.4);
    if (c === 'fall') {
      this.fallT = 0;
      if (this.knife) this.knife.visible = true;
    }
    if (c === 'heal') this.healT = 0;
    if (c === 'release') {
      this.releaseT = 0;
      this.ctx.shake(0.5);
    }
  }

  /* ---------- the board ---------- */

  apply(terrain: TerrainBuild, hour: Hour = 'day'): void {
    // The crown is its own: the board's tiles and the world beyond them hidden; the pieces stand on the wood.
    this.terrain = terrain;
    terrain.mesh.visible = false;
    terrain.decor.visible = false;
    if (terrain.fog) terrain.fog.group.visible = false;
    terrain.ground = () => 0;
    terrain.heights.fill(0);
    this.dome = new SkyDome();
    this.group.add(this.dome.mesh);
    this.paint(this.place === 'crownNight' ? 'night' : hour);
  }

  paint(hour: Hour, weather?: string): void {
    this.hour = this.place === 'crownNight' ? 'night' : hour;
    const look = HOURS[this.hour] ?? HOURS.day;
    const scene = this.ctx.scene;
    scene.background = new THREE.Color(look.bg);
    if (scene.fog instanceof THREE.Fog) {
      scene.fog.color.set(look.fog[0]);
      scene.fog.near = look.fog[1];
      scene.fog.far = look.fog[2];
    }
    const { hemi, key, rim } = this.ctx;
    if (hemi) {
      hemi.color.set(look.hemi[0]);
      hemi.groundColor.set(look.hemi[1]);
      hemi.intensity = look.hemi[2];
    }
    if (key) {
      key.color.set(look.key[0]);
      key.intensity = look.key[1];
    }
    if (rim) {
      rim.color.set(look.rim[0]);
      rim.intensity = look.rim[1];
    }
    this.dome?.set(look.sky[0], look.sky[1], look.sky[2]);
    if (this.dome) this.dome.u.uSunDir.value.set(0.5, this.hour === 'dusk' ? 0.1 : 0.45, -0.6);
    for (const m of [this.cloud, this.wisp]) {
      if (!m) continue;
      (m.uniforms.color as THREE.IUniform<THREE.Color>).value.set(look.cloud[0]);
      (m.uniforms.shade as THREE.IUniform<THREE.Color>).value.set(look.cloud[1]);
    }
    void weather;
  }

  tick(s: BattleState): void {
    for (const e of s._events ?? []) {
      if (e.t !== 'senda') continue;
      const p = this.ctx.at(e.x, e.y, 0.2);
      if (e.kind === 'creak') {
        // Snow dust coming off the bough above.
        this.ctx.vfx.burst(p.clone().setY(3.4), { n: 18, color: '#f2f6fa', speed: 0.6, up: -0.8, size: 0.1, life: 30, gravity: 0.6 });
      } else if (e.kind === 'snowfall') {
        this.ctx.shake(0.35);
        this.ctx.vfx.burst(p, { n: 34, color: '#f4f8fc', speed: 2.6, up: 1.6, size: 0.18, life: 22, gravity: 1 });
        this.ctx.vfx.ring(this.ctx.at(e.x, e.y, 0.06), 0.3, 1.8, '#eef4fa', 10, 0.9);
      } else if (e.kind === 'bridge') {
        this.ctx.vfx.burst(p, { n: 14, color: '#f4f8fc', speed: 1.2, up: 1.2, size: 0.1, life: 16, gravity: 1 });
      } else if (e.kind === 'flock') {
        this.ctx.vfx.burst(p.setY(2.4), { n: 10, color: '#1a1a20', speed: 1.4, up: 0.4, size: 0.12, life: 18 });
      } else if (e.kind === 'swell') {
        this.ctx.vfx.burst(p, { n: 8, color: '#eef4fa', speed: 0.6, up: 0.8, size: 0.08, life: 12, gravity: 1 });
      } else if (e.kind === 'burst') {
        this.ctx.shake(0.3);
        this.ctx.vfx.burst(p, { n: 26, color: '#4a3a30', speed: 2.4, up: 2.2, size: 0.13, life: 18, gravity: 1 });
        this.ctx.vfx.burst(p, { n: 14, color: '#eef4fa', speed: 1.8, up: 1.6, size: 0.1, life: 16, gravity: 1 });
      } else if (e.kind === 'tread') {
        this.ctx.flare(p.clone().setY(p.y + 0.4), '#a8e078', 2.2, 12);
        this.ctx.vfx.burst(p, { n: 18, color: '#8ed060', speed: 1.4, up: 1.8, size: 0.11, life: 14 });
        this.ctx.vfx.ring(this.ctx.at(e.x, e.y, 0.06), 0.3, 1.4, '#8ed060', 10, 0.9);
      } else if (e.kind === 'bandaged') {
        this.ctx.flare(p.setY(p.y + 1), '#f4f8fc', 3.5, 20);
      } else if (e.kind === 'knot') {
        const shut = e.npc === 'shut';
        this.ctx.flare(p.clone().setY(p.y + 0.5), shut ? '#d8e8f8' : '#ffc070', 2.4, 12);
        this.ctx.vfx.burst(p, { n: 16, color: shut ? '#cfe6fb' : '#ffae3c', speed: 1.4, up: 1.6, size: 0.1, life: 14 });
      } else if (e.kind === 'chill') {
        this.ctx.vfx.burst(p.setY(p.y + 0.6), { n: 12, color: '#bfe4ff', speed: 1.0, up: 1.2, size: 0.09, life: 14 });
      } else if (e.kind === 'pulse') {
        this.pulseAt = this.now;
      } else if (e.kind === 'budOpen') {
        this.ctx.flare(p.clone().setY(p.y + 0.6), '#b8f090', 2.6, 16);
        this.ctx.vfx.burst(p.setY(p.y + 0.5), { n: 16, color: '#8fdc5a', speed: 1.6, up: 1.8, size: 0.1, life: 18 });
      } else if (e.kind === 'budCrack') {
        this.ctx.flare(p.clone().setY(p.y + 0.6), '#ff8a6e', 2.4, 14);
        this.ctx.vfx.burst(p.setY(p.y + 0.5), { n: 22, color: '#ffae3c', speed: 2.0, up: 1.4, size: 0.1, life: 18, gravity: 1 });
      } else if (e.kind === 'crownRun') {
        this.runFlash = 1;
        this.ctx.shake(0.4);
        this.ctx.flare(p.clone().setY(p.y + 1.5), '#fff4d8', 5, 26);
      } else if (e.kind === 'frostWarn') {
        this.ctx.vfx.burst(p.setY(p.y + 0.4), { n: 14, color: '#bfe4ff', speed: 0.8, up: 1.0, size: 0.1, life: 18 });
      } else if (e.kind === 'frostOpen') {
        this.ctx.flare(p.clone().setY(p.y + 0.5), '#bfe4ff', 3, 16);
        this.ctx.vfx.burst(p, { n: 24, color: '#e8f4ff', speed: 1.8, up: 1.8, size: 0.12, life: 18 });
      } else if (e.kind === 'frostOff') {
        this.ctx.vfx.burst(p.setY(p.y + 0.8), { n: 26, color: '#e8f4ff', speed: 2.4, up: 2.0, size: 0.13, life: 18, gravity: 1 });
      } else if (e.kind === 'claw') {
        this.ctx.shake(0.5);
        this.ctx.vfx.burst(p, { n: 26, color: '#cfe6fb', speed: 2.6, up: 2.0, size: 0.15, life: 18, gravity: 1 });
      } else if (e.kind === 'top') {
        this.ctx.flare(p.setY(p.y + 1.2), '#ffffff', 4, 20);
      }
    }
  }

  draw(s: BattleState): void {
    this.lastState = s;
    if (this.Bl) this.drawBoughs(s);
    this.drawRings(s);
    this.drawBulges(s);
  }

  /** The fraction of a tick since the last (for what moves between ticks). */
  private tickAt = 0;
  private lastTick = -1;
  private frac(): number {
    return Math.max(0, Math.min(1, (this.now - this.tickAt) * 12));
  }

  /** The rings: a block coming down (red, closing), a swelling, the frost's claw, the frost on a knot, the gap's ends. */
  private drawRings(s: BattleState): void {
    const rings = this.rings;
    const w = s.senda?.winter;
    const r = s.rules?.senda?.winter;
    if (!rings || !w || !r) return;
    let q = 0;
    const ring = (p: THREE.Vector3, rad: number, color: THREE.Color): void => {
      if (q >= rings.instanceMatrix.count) return;
      this.m4.compose(this.v3.set(p.x, 0.05, p.z), this.q4.identity(), this.s3.set(rad, 1, rad));
      rings.setMatrixAt(q, this.m4);
      rings.setColorAt(q++, color);
    };
    const closing = (x: number, y: number, R: number, until: number, warn: number, a: THREE.Color, b: THREE.Color): void => {
      const k = Math.max(0, Math.min(1, (until - s.tick) / Math.max(1, warn)));
      const p = this.pt(x, y);
      ring(p, R, a);
      ring(p, R * (0.12 + 0.88 * k), b);
    };
    if (r.boughs) for (const b of w.blocks) closing(b[0] as number, b[1] as number, (r.boughs.snow.r / 1000) * S, b[2] as number, r.boughs.snow.warn, WARN, WARN2);
    if (r.serp)
      for (const id of Object.keys(w.sp)) {
        const m = w.sp[Number(id)] as number[];
        if (m[0] !== 1) continue;
        closing(m[2] as number, m[3] as number, (r.serp.r / 1000) * S, m[1] as number, r.serp.warn, WARN, m[4] === 0 ? WARN2 : WHITE);
      }
    if (r.sap) {
      if (w.claw >= 0) closing(w.cx, w.cy, (r.sap.boss.claw.r / 1000) * S, w.claw, r.sap.boss.claw.warn, WARN, WARN2);
      if (w.bwarn >= 0 && w.bknot >= 0) {
        const k = r.sap.knots[w.bknot];
        if (k) closing(k.x, k.y, 0.9, w.bwarn, r.sap.boss.warn, FROST, WHITE);
      }
    }
    rings.count = q;
    rings.instanceMatrix.needsUpdate = true;
    if (rings.instanceColor) rings.instanceColor.needsUpdate = true;
  }

  /** The bark swelling where a serpent comes out: a dome growing as it nears, snow cracking off it. */
  private drawBulges(s: BattleState): void {
    const bl = this.bulges;
    const w = s.senda?.winter;
    const r = s.rules?.senda?.winter?.serp;
    if (!bl || !w || !r) return;
    let n = 0;
    for (const id of Object.keys(w.sp)) {
      const m = w.sp[Number(id)] as number[];
      if (m[0] !== 1 || n >= bl.instanceMatrix.count) continue;
      const k = 1 - Math.max(0, Math.min(1, ((m[1] as number) - s.tick) / Math.max(1, r.warn)));
      const rad = 0.35 + 0.55 * k;
      this.m4.compose(this.pt(m[2] as number, m[3] as number, -0.02), this.q4.identity(), this.s3.set(rad * 1.6, rad * (0.6 + 0.6 * k), rad * 1.6));
      bl.setMatrixAt(n, this.m4);
      bl.setColorAt(n++, this.c3.set(BARK[1] as string).lerp(WHITE, 0.35 * (1 - k)));
    }
    bl.count = n;
    bl.instanceMatrix.needsUpdate = true;
    if (bl.instanceColor) bl.instanceColor.needsUpdate = true;
  }

  frame(now: number, camera?: THREE.Camera): void {
    const dt = this.now ? Math.min(0.1, now - this.now) : 0;
    this.now = now;
    const s = this.lastState;
    if (s && s.tick !== this.lastTick) {
      this.lastTick = s.tick;
      this.tickAt = now;
    }
    this.dome?.frame(now, camera);
    for (const m of [this.cloud, this.wisp, this.aurora]) if (m) (m.uniforms.time as THREE.IUniform<number>).value = now;
    if (camera) {
      lookPoint(camera, this.look);
      this.snow?.frame(now, this.look);
      this.burst?.frame(now, this.look);
    }
    const w = s?.senda?.winter;
    // The gaps' tips: apart they swing out and down on the wind; together they meet and lock.
    const br = s?.rules?.senda?.winter?.boughs;
    if (br && s)
      this.tips.forEach((tp, i) => {
        const t = s.waveT + this.frac();
        const open = gapOpenAt(br.sway, i, t);
        // Closing in the last `warn` before it opens, swinging apart in the first after it shuts.
        const near = gapOpenAt(br.sway, i, t + br.sway.warn);
        const target = open ? 0 : near ? 0.25 : 1;
        tp.ax += (target - tp.ax) * Math.min(1, dt * 4);
        const sw = Math.sin(now * 1.7 + i) * 0.08 * tp.ax;
        // Apart: each swings off sideways and dips toward the cloud; together, they reach and overlap.
        tp.a.rotation.set(0.45 * tp.ax, (1.25 + sw) * tp.ax, 0);
        tp.b.rotation.set(-0.45 * tp.ax, (-1.25 - sw) * tp.ax, 0);
      });
    // The sap's crowns (beads between ticks), the ice melting once the low crown runs.
    if (this.Sl && s) {
      this.drawSap(s, now);
      if (w && w.stage > 0) this.plugMelt = Math.min(1, this.plugMelt + dt * 0.5);
      if (this.plug) {
        this.plug.visible = this.plugMelt < 1;
        this.plug.scale.set(1 - this.plugMelt * 0.6, 1 - this.plugMelt, 1 - this.plugMelt * 0.6);
      }
    }
    // The blocks dropping their last half second.
    if (this.blocks && w && br && s) {
      let n = 0;
      for (const b of w.blocks) {
        const left = ((b[2] as number) - s.tick - this.frac()) / 12;
        if (left > 0.6 || n >= this.blocks.instanceMatrix.count) continue;
        const y = 0.3 + Math.max(0, left) * 6;
        this.m4.compose(this.pt(b[0] as number, b[1] as number, y), this.q4.setFromEuler(new THREE.Euler(left * 3, left * 2, 0)), this.s3.set(1.4, 1.0, 1.4));
        this.blocks.setMatrixAt(n, this.m4);
        this.blocks.setColorAt(n++, WHITE);
      }
      this.blocks.count = n;
      this.blocks.instanceMatrix.needsUpdate = true;
      if (this.blocks.instanceColor) this.blocks.instanceColor.needsUpdate = true;
    }
    // The top: the bough falling with Hob, the knife after it; the green of the cure; the snow let go.
    if (this.fallBough && this.fallT >= 0 && this.fallT < 8) {
      this.fallT += dt;
      const t = this.fallT;
      this.fallBough.rotation.z = -Math.min(1.2, t * 0.6);
      this.fallBough.rotation.y = t * 0.15;
      this.fallBough.position.y = -0.5 * 4 * t * t;
      if (this.knife) {
        this.knife.position.y = 0.9 - 2.2 * t * t;
        this.knife.rotation.set(t * 7, t * 3, 0);
      }
    }
    if (this.healT >= 0) this.healT += dt;
    if (this.releaseT >= 0) {
      this.releaseT += dt;
      if (this.burst) this.burst.opacity = Math.max(0, 0.95 - Math.max(0, this.releaseT - 3) * 0.2);
    }
    if (this.cloud && this.healT >= 0) (this.cloud.uniforms.open as THREE.IUniform<number>).value = Math.max(0, Math.min(1, this.healT * 0.6) - Math.max(0, this.healT - 6) * 0.3);
    this.runFlash = Math.max(0, this.runFlash - dt * 0.4);
    // The glow: the sap's beads and the open buds, the frost, the swellings, the fire's warmth at night.
    this.glow.begin();
    for (const l of this.lights) this.glow.add(l.p.x, l.p.y, l.p.z, l.c, l.size, l.a);
    if (this.Sl && w && s) {
      const L = this.Sl;
      L.nodes.forEach((n, i) => {
        if (!n.need) return;
        const p = this.nodeAt[i] as THREE.Vector3;
        const st = w.buds[i];
        if (st === 0 || n.stage < w.stage || w.done >= 0) this.glow.add(p.x, p.y + 0.6, p.z, GREEN, 1.6 + 0.3 * Math.sin(now * 4 + i), 0.6);
        else if (st === 1) this.glow.add(p.x, p.y + 0.6, p.z, AMBER, 1.4, 0.55);
      });
      // Each bead of sap glows warm over its dark groove.
      for (let i = 0; i < this.beadN; i++) {
        const b = this.beadAt[i] as THREE.Vector3;
        this.glow.add(b.x, b.y + 0.05, b.z, AMBER, 0.95, 0.75);
      }
      if (this.pulseAt >= 0 && now - this.pulseAt < 0.6) {
        const f = this.pt(L.foot.x, L.foot.y);
        this.glow.add(f.x, 0.4, f.z, AMBER, 2.4, 0.6 * (1 - (now - this.pulseAt) / 0.6));
      }
      if (this.runFlash > 0) {
        const root = this.nodeAt[L.roots[Math.max(0, w.stage - 1)] as number] ?? this.nodeAt[0];
        if (root) this.glow.add(root.x, 1.2, root.z, AMBER, 4 + 6 * this.runFlash, this.runFlash);
      }
    }
    if (this.healT >= 0 && this.healT < 12) {
      const k = Math.min(1, this.healT * 0.5) * Math.max(0, 1 - Math.max(0, this.healT - 8) * 0.25);
      for (let i = 0; i < 6; i++) this.glow.add(-1 + i * 1.1, 0.3, 0.4 - i * 0.3, GREEN, 2.2, 0.55 * k);
    }
    this.glow.end();
  }

  /** The knots' world points (the HUD's offer). */
  knotPoints(): THREE.Vector3[] {
    return this.knotAt.map((p) => p.clone().setY(0.4));
  }

  dispose(): void {
    for (const m of this.mats) m.dispose();
    for (const g of this.geos) g.dispose();
    this.dome?.dispose();
    this.snow?.dispose();
    this.burst?.dispose();
    this.glow.mesh.geometry.dispose();
    this.glow.material.dispose();
    this.group.removeFromParent();
    void this.terrain;
    void this.map;
    void this.hour;
  }
}
